import { useState, useEffect, useMemo, useCallback} from 'react';
import '../../../borrower/components/PreApprovalForm/PreApprovalForm.scss';
import './ProcessingTeamForm.scss';
import { TextInput, InputAutocompleteAddress, OptionDropdownInner } from '../../../../shared/components/ui/atoms';
import { FooterContent } from '../../../../features/onboarding/components';
import { AddressCaptionInner, formatPhoneNumber, validatePhoneNumber } from '../../../../features/borrower/components';
import { useGetBorrowers } from '../../../broker/hooks/useBrokerQueries';
import { getDisabledStateCodes } from '../../../../utils/constants';
import { debounce } from 'lodash';
import { formatAddressWithoutCountry } from '../../../../utils/functions';

interface ProcessingTeamFormProps {
  values: any;
  onContinue: (userData: any) => void;
  borrowerData: any;
  setBorrowerData: (data: any) => void;
}

export const ProcessingTeamForm = (props: ProcessingTeamFormProps) => {
  const { values, onContinue, borrowerData, setBorrowerData } = props

  const [firstName, setFirstName] = useState(() => borrowerData?.name || "")
  const [lastName, setLastName] = useState(() => borrowerData?.lastName || "")
  const [email, setEmail] = useState(() => borrowerData?.email || "")
  const [phoneNumber, setPhoneNumber] = useState(() => borrowerData?.phone || "")
  const [password] = useState("password")
  const [investmentAddress, setInvestmentAddress] = useState<string>("")
  const [investmentAddressFormatted, setInvestmentAddressFormatted] = useState('')
  const [hasSelectedAddress, setHasSelectedAddress] = useState<boolean>(false)
  const [addressSelected, setAddressSelected] = useState(false)
  const [isExistingBorrower, setIsExistingBorrower] = useState(!!borrowerData)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("")
  const [errors, setErrors] = useState<{
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phoneNumber: string | null;
    investmentAddress: string | null;
  }>({
    firstName: null,
    lastName: null,
    email: null,
    phoneNumber: null,
    investmentAddress: null
  })

  const [selectedBorrowerEmail, setSelectedBorrowerEmail] = useState(() => {
    if (borrowerData) return borrowerData.email
    if (borrowerData === null) return "new"
    return null
  })

  const { borrowers, hasMore, loadMore, isLoadingMore } = useGetBorrowers(debouncedSearchTerm)

  // Debounce para la búsqueda (500ms)
  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => {
      setDebouncedSearchTerm(value)
    }, 500),
    []
  )

  // Limpiar debounce al desmontar
  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel()
    }
  }, [debouncedSetSearch])

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value)
    debouncedSetSearch(value)
  }, [debouncedSetSearch])

  const options: Array<{
    value: string;
    label: string;
    disabled: boolean;
    className?: string;
    raw: any;
  }> = [
    ...borrowers
      .filter((v) => v.email) // Filtrar borrowers sin email
      .map((v) => ({
        value: v.email!,
        label: `${v.name} ${v.lastName}`,
        disabled: false,
        raw: v
      })),
    {
      value: "new",
      label: "New Borrower",
      disabled: false,
      className: 'new-borrower',
      raw: null
    }
  ]

  const userData = useMemo(() => ({
    firstName,
    lastName,
    email,
    password,
    phoneNumber,
    investmentAddress,
    isNewBorrower: !isExistingBorrower
  }), [firstName, lastName, email, password, phoneNumber, investmentAddress, isExistingBorrower])

  const clearAllErrors = () => {
    setErrors({
      firstName: null,
      lastName: null,
      email: null,
      phoneNumber: null,
      investmentAddress: null
    });
  };

  const validateFirstName = (value: string) => {
    if (!value || value.trim() === "") return "First name is required";
    return null;
  };

  const validateLastName = (value: string) => {
    if (!value || value.trim() === "") return "Last name is required";
    return null;
  };

  const validateEmail = (value: string) => {
    if (!value || value.trim() === "") return "Email address is required";
    const emailRegex = /\S+@\S+\.\S+/;
    if (!emailRegex.test(value)) return "Invalid email address";
    return null;
  };

  const isFormValid = () => {
    // Validar dirección siempre
    if (!hasSelectedAddress || !investmentAddress || investmentAddress.trim() === '') {
      return false;
    }
    
    // Si es borrower existente, solo se requiere dirección válida
    if (isExistingBorrower) return true;
    
    const firstNameError = validateFirstName(firstName);
    const lastNameError = validateLastName(lastName);
    const emailError = validateEmail(email);
    const phoneError = validatePhoneNumber(phoneNumber);
    
    return !firstNameError && !lastNameError && !emailError && !phoneError;
  };

  // Sincronizar formulario cuando cambia borrowerData
  useEffect(() => {
    if (borrowerData) {
      setSelectedBorrowerEmail(borrowerData.email)
      setIsExistingBorrower(true)
      setFirstName(borrowerData.name || "")
      setLastName(borrowerData.lastName || "")
      setEmail(borrowerData.email || "")
      setPhoneNumber(borrowerData.phone || "")
    } else {
      setSelectedBorrowerEmail("new")
      setIsExistingBorrower(false)
      setFirstName("")
      setLastName("")
      setEmail("")
      setPhoneNumber("")
    }
    setInvestmentAddress("")
    setHasSelectedAddress(false)
    clearAllErrors()
  }, [borrowerData])

  const onBorrowerChange = (v: string) => {
    setSelectedBorrowerEmail(v)
    const selected = options.find((o) => o.value === v)?.raw || null
    
    if (selected) {
      setFirstName(selected.name || "")
      setLastName(selected.lastName || "")
      setEmail(selected.email || "")
      setPhoneNumber(selected.phone || "")
      setIsExistingBorrower(true)
      setBorrowerData(selected)
    } else {
      setFirstName("")
      setLastName("")
      setEmail("")
      setPhoneNumber("")
      setIsExistingBorrower(false)
      setBorrowerData(null)
    }
    clearAllErrors()
  }

  const handleFirstNameChange = (value: string) => {
    if (isExistingBorrower) return;
    setFirstName(value);
    const error = validateFirstName(value);
    setErrors(prev => ({ ...prev, firstName: error }));
  };

  const handleLastNameChange = (value: string) => {
    if (isExistingBorrower) return;
    setLastName(value);
    const error = validateLastName(value);
    setErrors(prev => ({ ...prev, lastName: error }));
  };

  const handleEmailChange = (value: string) => {
    if (isExistingBorrower) return;
    setEmail(value);
    const error = validateEmail(value);
    setErrors(prev => ({ ...prev, email: error }));
  };

  const handlePhoneNumberChange = (value: string) => {
    if (isExistingBorrower) return;
    
    const formattedValue = formatPhoneNumber(value);
    setPhoneNumber(formattedValue);
    
    const phoneError = validatePhoneNumber(formattedValue);
    setErrors(prev => ({
      ...prev,
      phoneNumber: phoneError
    }));
  };

  const validateAllAndSetErrors = () => {
    // Validar dirección para todos los casos
    const investmentAddressError =
      !hasSelectedAddress || !investmentAddress || investmentAddress.trim() === ''
        ? 'Investment property address is required'
        : null;

    if (isExistingBorrower) {
      setErrors(prev => ({
        ...prev,
        investmentAddress: investmentAddressError
      }));
      return;
    }

    const firstNameError = validateFirstName(firstName);
    const lastNameError = validateLastName(lastName);
    const emailError = validateEmail(email);
    const phoneError = validatePhoneNumber(phoneNumber);

    setErrors({
      firstName: firstNameError,
      lastName: lastNameError,
      email: emailError,
      phoneNumber: phoneError,
      investmentAddress: investmentAddressError
    });
  };

  const handleSubmitClick = () => {
    if (!isFormValid()) {
      validateAllAndSetErrors();
      return;
    }

    const currentUserData = {
      firstName,
      lastName,
      email: typeof email === 'string' ? email.trim().toLowerCase() : email,
      password,
      phoneNumber,
      investmentAddress: investmentAddressFormatted || investmentAddress,
      isNewBorrower: !isExistingBorrower
    };

    onContinue(currentUserData);
  };

  const titleMarkup = borrowerData ? (
    <h1>{`Confirm the account info and we'll add the loan to the account`}</h1>
  ) : (
    <h1>{`Fill out borrower info and generate account creation email`}</h1>
  )

  return (
    <div className='pre-approval-form processing'>
      <div className='first-section'>
        <p>
          New loan for {" "}
          <div style={{ display: 'inline-block' }}>
            <OptionDropdownInner
              isProcessingTeamMode={true}
              onSearch={handleSearch}
              options={options}
              defaultValue={options[0]?.value}
              value={selectedBorrowerEmail}
              onChange={onBorrowerChange}
              hasMore={hasMore}
              onLoadMore={loadMore}
              isLoadingMore={isLoadingMore}
            />
          </div>
        </p>
        {titleMarkup}
        <div className='inputs-container'>
          <TextInput 
            label="First name" 
            value={firstName} 
            onChange={handleFirstNameChange} 
            error={errors.firstName || undefined} 
          />
          <TextInput 
            label="Last name" 
            value={lastName} 
            onChange={handleLastNameChange} 
            error={errors.lastName || undefined} 
          />
          <TextInput 
            label="Email address" 
            value={email} 
            onChange={handleEmailChange} 
            error={errors.email || undefined} 
          />
          <TextInput 
            label="Phone number" 
            value={phoneNumber} 
            onChange={handlePhoneNumberChange} 
            error={errors.phoneNumber || undefined} 
          />
        </div>

        <div className='property'>
          <InputAutocompleteAddress
            value={investmentAddress}
            onChange={(address: string) => {
              setInvestmentAddress(address)
              setHasSelectedAddress(false)
            }}
            onSelect={(data: any) => {
              const selectedAddress = data.selectedAddress || data.formatted_address;
              const displayAddress = formatAddressWithoutCountry(selectedAddress);
              setInvestmentAddress(displayAddress);
              setInvestmentAddressFormatted(formatAddressWithoutCountry(data.formatted_address));

              setHasSelectedAddress(true);
            }}
            error={errors.investmentAddress || undefined}
            excludedStates={getDisabledStateCodes()}
          />
          <AddressCaptionInner {
            ...{ 
              addressSelected, 
              setAddressSelected, 
              setInvestmentAddress, 
              caption: "The borrower doesn't have specific property in mind",
              setHasSelectedInvestmentAddress: setHasSelectedAddress,
              setHasManuallyEditedInvestmentAddress: () => {}
            } as any
          } />
        </div>
      </div>

      {!addressSelected && (
        <div className='resume-container'>
          <FooterContent 
            values={values} 
            oneLine={true} 
            isSpan={true} 
            isProcessingTeamMode={true} 
          />
        </div>
      )}

      <div>
        <div 
          className={`loan-options-button ${!isFormValid() ? 'disabled' : ''}`} 
          onClick={handleSubmitClick}
        >
          Email borrower account creation link
        </div>
        <span className='caption'>Borrower agrees to be contacted by Constitution Lending</span>
      </div>
    </div>
  );
}